"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { CheckCircle2, X, XCircle } from "lucide-react"

import { cn } from "@/lib/utils"

export type ToastKind = "success" | "error"

export type ToastPayload = {
  kind: ToastKind
  message: string
}

type ToastContextValue = {
  toast: ToastPayload | null
  showToast: (toast: ToastPayload) => void
  dismissToast: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const AUTO_DISMISS_MS = 3500

/** Title-case each whitespace-separated word for toast event copy. */
function titleCaseToastMessage(message: string): string {
  return message
    .trim()
    .split(/\s+/)
    .map((word) =>
      word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ")
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastPayload | null>(null)

  const showToast = useCallback((next: ToastPayload) => {
    setToast({
      ...next,
      message: titleCaseToastMessage(next.message),
    })
  }, [])

  const dismissToast = useCallback(() => {
    setToast(null)
  }, [])

  const value = useMemo(
    () => ({ toast, showToast, dismissToast }),
    [toast, showToast, dismissToast],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <StandardToaster />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider")
  }
  return ctx
}

/** App-wide toast: over the topmenu bottom bar, just left of the user profile. */
function StandardToaster() {
  const { toast, dismissToast } = useToast()

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => dismissToast(), AUTO_DISMISS_MS)
    return () => clearTimeout(t)
  }, [toast, dismissToast])

  if (!toast) return null

  const Icon = toast.kind === "success" ? CheckCircle2 : XCircle

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed z-[60] flex items-center justify-end px-0"
      style={{
        top: "var(--topmenu-top-height, 3.14rem)",
        height: "var(--topmenu-bottom-height, 2.65rem)",
        // Leave room for the right profile column (min-w 12.5rem + px-3 sides).
        right: "calc(12.5rem + 1.5rem)",
      }}
    >
      <div
        className={cn(
          "pointer-events-auto flex max-w-md items-start gap-3 rounded-xl border bg-white px-4 py-2 shadow-lg ring-1 ring-black/5 dark:bg-slate-900",
          toast.kind === "success"
            ? "border-emerald-200 dark:border-emerald-900/50"
            : "border-rose-200 dark:border-rose-900/50",
        )}
      >
        <Icon
          className={cn(
            "mt-0.5 size-5 shrink-0",
            toast.kind === "success" ? "text-emerald-500" : "text-rose-500",
          )}
        />
        <p className="flex-1 text-sm text-slate-700 dark:text-slate-200">{toast.message}</p>
        <button
          type="button"
          onClick={dismissToast}
          aria-label="Dismiss"
          className="-mr-1 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  )
}
